/**
 * Generated Coupons List — shows coupon codes with expiry status.
 *
 * Embedded in MonthlyCouponsTab below the config section.
 * Feature 4 — Coupon Expiry Display.
 */

import { useState } from "react";
import { Ticket, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGeneratedCoupons } from "../api/rewardsAdminHooks";

const PAGE_SIZE = 20;

const statusConfig = {
  active: {
    label: "ใช้งานได้",
    variant: "secondary" as const,
    icon: CheckCircle2,
    className: "text-green-700 border-green-300 bg-green-50",
  },
  expiring_soon: {
    label: "ใกล้หมดอายุ",
    variant: "outline" as const,
    icon: AlertTriangle,
    className: "text-orange-700 border-orange-300 bg-orange-50",
  },
  expired: {
    label: "หมดอายุ",
    variant: "destructive" as const,
    icon: Clock,
    className: "",
  },
};

export function GeneratedCouponsList() {
  const [offset, setOffset] = useState(0);
  const couponsQuery = useGeneratedCoupons(PAGE_SIZE, offset);

  if (couponsQuery.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }

  const coupons = couponsQuery.data?.coupons ?? [];
  const hasMore = couponsQuery.data?.has_more ?? false;

  if (coupons.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-6 text-center">
        <Ticket className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          ยังไม่มีคูปองที่สร้าง
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {coupons.map((coupon) => {
        const status = statusConfig[coupon.expiry_status] || statusConfig.active;
        const StatusIcon = status.icon;
        return (
          <div
            key={coupon.name}
            className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium font-mono truncate">
                {coupon.coupon_code}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {coupon.pricing_rule}
              </p>
            </div>
            <span className="text-xs text-muted-foreground shrink-0">
              ใช้แล้ว {coupon.used}/{coupon.maximum_use || "∞"}
            </span>
            {coupon.valid_upto && (
              <span className="text-xs text-muted-foreground shrink-0">
                ถึง {coupon.valid_upto}
              </span>
            )}
            <Badge
              variant={status.variant}
              className={`text-xs shrink-0 gap-1 ${status.className}`}
            >
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </Badge>
          </div>
        );
      })}

      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
          disabled={couponsQuery.isFetching}
          className="w-full"
        >
          {couponsQuery.isFetching ? "กำลังโหลด..." : "โหลดเพิ่มเติม"}
        </Button>
      )}
    </div>
  );
}
